import React, {Fragment, useState, useEffect} from "react"
import styled from "styled-components"
import moment from "moment"
import {useAuth0} from "@auth0/auth0-react"
import {Container, Grid, Row, Col} from "@uprise/grid"
import {TextInputHorizontal} from "@uprise/form"
import {H4, H6, H5} from "@uprise/headings"
import {P} from "@uprise/text"
import {Button} from "@uprise/button"
import {useForm, Controller, useFieldArray} from "react-hook-form"
import {useSelector, useDispatch} from "react-redux"
import {authSelector} from "features/Auth/authSlice"
import {employerSelector, fetchMasterCodes} from "features/Employers/employerSlice"
import {extended, semantic} from "@uprise/colors"
import {settingsSelector, fetchEAPCenters} from "features/Settings/settingsSlice"

import {salesManager, accountManager, companyIndustry, australianStates} from "helpers/constants"
import {calcFinalCaps} from "helpers/utils"
import DropDown from "components/shared/DropDown"
import Datepicker from "components/shared/DatePicker"
import AutoComplete from "components/shared/AutoComplete"
import Radio from "components/shared/Radio"
import Toggle from "components/shared/Toggle"

// assets
import WarnIcon from "assets/images/png/warn.png"

const FormElementContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: ${props => (props.error ? "1px solid #ff9999" : "1px solid #edeafa")};
`

const NumberInputContainer = styled(FormElementContainer)`
	width: unset;
`

const CheckBoxFormElementContainer = styled.div`
	width: 60%;
	display: flex;
	margin: 10px 0 10px auto;
	justify-content: space-between;
	align-items: center;
	border-bottom: 1px solid #edeafa;
`

const FormElementLabel = styled(H6)`
	margin: 16px 0 8px 0;

	&:after {
		content: "${props => (props.isRequired ? "*" : "")}";
	}
`

const FormElementPlanLabel = styled(H6)`
	margin: 16px 0 8px 0;
	text-align: left;
	&:after {
		content: "${props => (props.isRequired ? "*" : "")}";
	}
`

const FormInputContainer = styled.div`
	flex-basis: 60%;
	position: relative;
	align-self: center;
`

const ToggleContainer = styled(FormInputContainer)`
	display: flex;
	justify-content: flext-start;
`

const FormInputInlineContainer = styled.div`
	display: flex;
	flex-direction: row;
`

const FormTextInput = styled(TextInputHorizontal)``

const FormNumberInputFixedWidth = styled(TextInputHorizontal)`
	width: 80px;
	height: 24px;
	padding: 2px 2px 2px 8px;
	border-radius: 4px;
	border: solid 1px #d4cbfb;
`

const FormTextArea = styled.textarea`
	border: none;
	resize: none;
	width: 100%;
`

const ButtonContainer = styled.div`
	display: flex;
`

const PrimaryButton = styled(Button)`
	width: 100%;
	min-width: 100px;
	height: 50px;
	margin: 10px 6px 0;
	outline: none;
`

const SecondaryButton = styled(Button)`
	width: 100%;
	min-width: 100px;
	height: 50px;
	margin: 10px 6px 0;
	border-color: transparent;
	background-color: #f6f4fc;
	outline: none;
`

const NavigationButton = styled(Button)`
	width: 100%;
	min-width: 100px;
	height: 50px;
	margin: 10px 6px 0;
`

const FormSpan = styled.span``

const FormSpanText = styled(P)`
	margin: 0 0 0 10px;
`

const FormSpanLabel = styled.label`
	display: flex;
	padding: 10px;
	padding-left: 0;
`

const FormRightItemContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: flex-end;
	align-items: center;
	border-bottom: ${props => (props.error ? "1px solid #ff9999" : "1px solid #edeafa")};
`

const FormRightContainer = styled.div`
	display: flex;
	flex-basis: 60%;
	justify-content: space-between;
`

const PlanContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: ${props => (props.error ? "1px solid #ff9999" : "1px solid #edeafa")};
`

const FeaturePricingInputContainer = styled.div`
	align-self: center;
`

const MessageStyles = styled.label`
	font-size: 12px;
	color: ${props => {
		if (props.focused) {
			return `${extended.blue.one}`
		} else if (props.validation) {
			return `${semantic.error}`
		} else {
			return `${extended.purple.five}`
		}
	}};
	display: block;
	margin-top: 8px;
	transition: font-size 0.2s;
`

const MessageStylesCaps = styled(MessageStyles)`
	padding-left: 40%;
	text-align: left;
`

const TextInputWrap = styled.div`
	width: 60%;
	margin-left: 40%;
	display: flex;
`

const RadioWrap = styled.div`
	display: flex;
`

const ErrorWrap = styled.div`
	font-size: 12px;
	color: ${semantic.error};
	display: block;
	margin-top: 8px;
	transition: font-size 0.2s;
	display: flex;
	align-items: center;
}
`

const WarnWrap = styled.img`
	height: 16px;
	margin-right: 4px;
`

export const ButtonSmall = styled(Button)`
	font-size: 14px;
	font-weight: normal;
	font-family: Proxima Nova;
	//margin-right: 15px;
	background-color: #f6f4fc;
	padding: 2px 15px;
`

export const ButtonWarning = styled(ButtonSmall)`
	color: #e52121;
	background-color: background-color: rgba(229, 33, 33, 0.08);
	margin-right: unset;
	&:hover{
		color: #e52121;
	}

`

const EditEmployerDetails = ({data, employerCode, capDetails, expiryDate, onComplete, onCancel, domains}) => {
	const {masterCodes, courses} = useSelector(employerSelector)
	const authData = useSelector(authSelector)

	const {eapCenters} = useSelector(settingsSelector)
	const [eapdata, seteapData] = useState([])
	const [isError, setIsError] = useState(false)
	const [confirmDeleteIdx, setConfirmDeleteIdx] = useState()
	const dispatch = useDispatch()
	const {getAccessTokenSilently} = useAuth0()
	const {register, control, handleSubmit, watch, reset, trigger, setError, errors, setValue} = useForm({
		// defaultValues: {}; you can populate the fields by this attribute
		mode: "onChange",
		reValidateMode: "onChange"
	})

	const {fields, append, prepend, remove, swap, move, insert} = useFieldArray({
		control,
		name: "contact"
	})

	const {fields: featurePricingFields, append: featurePricingAppend} = useFieldArray({
		control,
		name: "featurePricing"
	})

	const watchEAPStatus = watch("eapStatus")

	const watchBudgetAlert = watch("budgetAlert", "")
	const watchCodeType = watch("codeType")
	const showPricingType = watch("pricingType")
	const watchAccountManager = watch("accountManager")

	const watchFeaturePricing = featurePricingFields.length > 0 ? watch("featurePricing") : []

	useEffect(() => {
		async function fetchData() {
			const token = await getAccessTokenSilently()
			dispatch(fetchMasterCodes({token}))
		}

		fetchData()
	}, [])
	useEffect(() => {
		if (data.contact && data.contact.length > 0) {
			data.contact.map(item => {
				append(item)
			})
		}

		if (data.plan.featurePricing && data.plan.featurePricing.length > 0) {
			data.plan.featurePricing.map(item => {
				featurePricingAppend(item)
				return null
			})
			const isCareNavigatorExists = data.plan.featurePricing.some(feature => feature.id === "care-navigator")
			const additionalSessionExists = data.plan.featurePricing.some(
				feature => feature.id === "additional-sessions"
			)

			if (!isCareNavigatorExists) {
				featurePricingAppend({
					enabled: true,
					fee: "",
					label: "Care Navigator",
					id: "care-navigator",
					per: "hour"
				})
			}
			if (!additionalSessionExists) {
				featurePricingAppend({
					enabled: true,
					fee: "",
					label: "Additonal Session Request",
					id: "additional-sessions",
					per: "hour"
				})
			}
		}
	}, [data])

	const fetchEAPData = async () => {
		const token = await getAccessTokenSilently()
		dispatch(
			fetchEAPCenters({
				token
			})
		)
	}
	useEffect(() => {
		if (watchEAPStatus === "ibh") {
			if (eapCenters && Object.entries(eapCenters.data).length === 0) {
				fetchEAPData()
			}
		}
	}, [watchEAPStatus])

	useEffect(() => {
		if (eapCenters.data) {
			const data = eapCenters.data.map(item => {
				return {
					label: item.name,
					value: item.name,
					phone: item.eapPhone
				}
			})

			seteapData(data)
		}
	}, [eapCenters])

	const _handleEAPNameChange = item => {
		if (item) {
			setValue("eapphone", item.phone)
		}
	}

	const updateContactFields = () => {
		fields.map((item, index) => {
			setValue(`contact[${index}].firstName`, item.firstName, {shouldValidate: true})
			setValue(`contact[${index}].lastName`, item.lastName, {shouldValidate: true})
			setValue(`contact[${index}].budgetAlert`, item.budgetAlert, {shouldValidate: true})
			setValue(`contact[${index}].contactFunction`, item.contactFunction, {shouldValidate: true})
			setValue(`contact[${index}].contactTier`, item.contactTier, {shouldValidate: true})
			setValue(`contact[${index}].emailAddress`, item.emailAddress, {shouldValidate: true})
			setValue(`contact[${index}].phoneNumber`, item.phoneNumber, {shouldValidate: true})
			setValue(`contact[${index}].positionTitle`, item.positionTitle, {shouldValidate: true})
		})
	}

	const handleAddContact = e => {
		e.preventDefault()
		append({
			firstName: "",
			lastName: "",
			positionTitle: "",
			phoneNumber: "",
			emailAddress: "",
			contactFunction: "",
			contactTier: "",
			budgetAlert: ""
		})
	}

	const handleDeleteContact = (e, idx) => {
		e.preventDefault()
		remove(idx)
		setConfirmDeleteIdx(idx)
	}

	useEffect(() => {
		updateContactFields()
	}, [fields.length])

	const callCapPeriod = capDetails.callCapPeriod
	const [combinedBookingCaps, setCombinedBookingCaps] = useState()

	useEffect(() => {
		const latestCaps = calcFinalCaps(callCapPeriod)
		if (latestCaps.combinedBookingCaps > 0) {
			setValue("coachingType", "combined", {shouldValidate: true})
			setValue("combinedBookingCaps", latestCaps.combinedBookingCaps, {shouldValidate: true})
		} else {
			setValue("coachingType", "individual", {shouldValidate: true})
			setValue("therapyBookingCaps", latestCaps.therapyBookingCaps, {shouldValidate: true})
			setValue("coachingBookingCaps", latestCaps.coachingBookingCaps, {shouldValidate: true})
		}
		if (data) {
			const am = accountManager.filter(state => state.value.includes(data.general.accountManager))
			if (am.length) setValue("accountManager", am[0].value, {shouldValidate: true})
			else if (data.general.accountManager) {
				setValue("accountManager", "Other", {shouldValidate: true})
				setValue("other", data.general.accountManager, {shouldValidate: true})
			}
		}
	}, [])

	const watchSessionCap = watch(["coachingType"])
	const watchCodeDates = watch(["dateSigned", "expiryDate"])
	const watchPlanDates = watch(["codeStartDate", "codeExpiryDate"])
	const watchCapCalls = watch(["capCalls"])

	useEffect(() => {
		if (!watchFeaturePricing?.[3]) return
		if (!watchFeaturePricing?.[3]?.enabled && !watchFeaturePricing?.[2]?.enabled) {
			setIsError(true)
			setValue("combinedBookingCaps", 0, {shouldValidate: true})
			setValue("therapyBookingCaps", 0, {shouldValidate: true})
			setValue("coachingBookingCaps", 0, {shouldValidate: true})
		} else {
			setIsError(false)
		}
	}, [watchFeaturePricing])

	const codeDateCheck = () => {
		return !!(watchCodeDates.dateSigned && (watchCodeDates?.dateSigned).diff(watchCodeDates.expiryDate) > 0)
			? "Date Signed cant exceed Date of Expiry"
			: true
	}
	const codeDateCheckExpiry = () => {
		return !!(watchCodeDates.expiryDate && (watchCodeDates?.expiryDate).diff(watchCodeDates.dateSigned) < 0)
			? "Date of Expiry cant be before Date Signed"
			: true
	}
	const planDateCheck = () => {
		return !!(
			watchPlanDates.codeStartDate && (watchPlanDates?.codeStartDate).diff(watchPlanDates.codeExpiryDate) > 0
		)
			? "Code Start Date cant exceed Code Expiry Date"
			: true
	}
	const planDateCheckExpiry = () => {
		return !!(
			watchPlanDates.codeExpiryDate && (watchPlanDates?.codeExpiryDate).diff(watchPlanDates.codeStartDate) < 0
		)
			? "Code Expiry Date cant be before Code Start Date"
			: true
	}

	const validateName = name => {
		return !name.trim() ? "Field can not be left empty" : true
	}

	const [latestCapRecords, setLatestCapRecords] = useState(calcFinalCaps(callCapPeriod))

	return (
		<Container>
			<form
				onSubmit={handleSubmit(formData => {
					let latestCaps = calcFinalCaps(callCapPeriod)

					if (!latestCaps?.startingDate) latestCaps.startingDate = formData.codeStartDate

					if (typeof formData.combinedBookingCaps === "number") {
						latestCaps = {
							...latestCaps,
							combinedBookingCaps: callCapPeriod?.[callCapPeriod?.length - 1]?.combinedBookingCaps,
							therapyBookingCaps: parseInt(formData.therapyBookingCaps) || 0,
							coachingBookingCaps: parseInt(formData.coachingBookingCaps) || 0,
							updatedAt: moment().toISOString(),
							updatedBy: authData.data.email
						}
					} else {
						latestCaps = {
							...latestCaps,
							combinedBookingCaps: parseInt(formData.combinedBookingCaps) || 0,
							therapyBookingCaps: parseInt(formData.therapyBookingCaps) || 0,
							coachingBookingCaps: parseInt(formData.coachingBookingCaps) || 0,
							updatedAt: moment().toISOString(),
							updatedBy: authData.data.email
						}
					}

					let updatedCaps = [...callCapPeriod]
					const {idx, ...caps} = latestCaps

					if (idx !== -1) {
						updatedCaps[latestCaps.idx] = caps
					} else updatedCaps[updatedCaps.length - 1] = caps

					if (formData.accountManager === "Other") formData.accountManager = formData.other

					let finalData = {
						code: formData.employerCode,
						expiryDate: formData.codeExpiryDate,
						data: {
							general: {
								companyName: formData.companyName,
								companyIndustry: formData.companyIndustry,
								size: formData.size,
								phoneNumber: formData.phoneNumber,
								address: formData.address,
								state: formData.state,
								city: formData.city,
								postCode: formData.postCode,
								description: formData.description,
								salesManager: formData.salesManager,
								eapStatus: formData.eapStatus,
								eapname: formData.eapname,
								eapphone: formData.eapphone,
								eapwebsite: formData.eapwebsite,
								launchDate: formData.launchDate,
								accountManager: formData.accountManager,
								notes: formData.notes
							},
							contract: {
								contractStatus: formData.contractStatus,
								dateSigned: formData.dateSigned,
								expiryDate: formData.expiryDate,
								pipedrive: formData.pipedrive,
								gdrive: formData.gdrive
							},
							contact: formData.contact,
							code: {
								//capCalls: data.capCalls,
								//employerCode: formData.employerCode,
								codeType: formData.codeType,
								masterCode: formData.masterCode,
								codeLabel: formData.codeLabel,
								codeStartDate: formData.codeStartDate,
								course: formData.course,
								maxUser: formData.maxUser,
								status: data.code.status,
								budgetAlert: formData.budgetAlert,
								budgetAmount: formData.budgetAmount,
								budgetHours: formData.budgetHours,
								budgetexceedAction: formData.budgetexceedAction
							},
							plan: {
								pricingType: formData.pricingType,
								price: formData.price,
								licenses: formData.licenses,
								featurePricing: formData.featurePricing,
								addOnType: formData.addOnType,
								prePurchased: formData.prePurchased,
								prePurchasedPrice: formData.prePurchasedPrice
							}
						},
						caps: {
							capCalls: formData.capCalls,
							callCapPeriod: [...updatedCaps]
						},
						domains: formData.domains ? [...formData.domains.split(", ")] : []
					}

					onComplete(finalData)
				})}
			>
				<Row>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer error={errors.companyName?.type === "required"}>
							<FormElementLabel isRequired={true}>Company Name</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"companyName"}
									control={control}
									rules={{
										required: {
											value: true,
											message: "Company Name is Required"
										},
										maxLength: {
											value: 100,
											message: "Company name can not exceed 100 characters"
										}
									}}
									defaultValue={data.general.companyName}
									as={<FormTextInput id={"companyName"} type='text' placeholder='Company Name' />}
								/>
							</FormInputContainer>
						</FormElementContainer>

						{errors["companyName"] && errors["companyName"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["companyName"]}>
								{errors["companyName"] && errors["companyName"].message}
							</MessageStyles>
						) : null}

						<FormElementContainer>
							<FormElementLabel>Company Industry</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"companyIndustry"}
									control={control}
									defaultValue={data.general.companyIndustry}
									render={props => (
										<DropDown
											isFirst={true}
											items={companyIndustry}
											label='Select Item'
											variant='primary'
											onChange={item => props.onChange(item.value)}
											initialSelectedItem={
												data
													? companyIndustry.filter(industry => {
															return (
																(industry.value &&
																	industry.value.includes(
																		data.general.companyIndustry
																	)) ||
																(data.general.companyIndustry &&
																	data.general.companyIndustry.includes(
																		industry.value
																	))
															)
													  })[0]
													: data.general.companyIndustry
													? {
															label: data.general.companyIndustry,
															value: data.general.companyIndustry
													  }
													: ""
											}
											menuStyle={{position: "absolute", zIndex: 2}}
										/>
									)}
								/>
							</FormInputContainer>
						</FormElementContainer>

						<FormElementContainer error={errors.size?.type === "required"}>
							<FormElementLabel isRequired={true}>Size</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"size"}
									control={control}
									defaultValue={data.general.size}
									rules={{
										required: {
											value: true,
											message: "Company Size is Required"
										},
										min: {
											value: 1,
											message: "Company size should be at least 1"
										}
									}}
									as={<FormTextInput id={"size"} type='number' placeholder='Company Size' />}
								/>
							</FormInputContainer>
						</FormElementContainer>
						{errors["size"] && errors["size"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["size"]}>
								{errors["size"] && errors["size"].message}
							</MessageStyles>
						) : null}

						<FormElementContainer>
							<FormElementLabel>Phone Number</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"phoneNumber"}
									control={control}
									rules={{
										pattern: {
											value: /^\+?[\d ]+$/g,
											message: "Phone number should be valid"
										},
										maxLength: {
											value: 15,
											message: "Phone Number can be a maximum of 15 characters"
										}
									}}
									defaultValue={data.general.phoneNumber}
									as={<FormTextInput id={"phoneNumber"} type='tel' placeholder='Phone Number' />}
								/>
							</FormInputContainer>
						</FormElementContainer>
						{errors["phoneNumber"] && errors["phoneNumber"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["phoneNumber"]}>
								{errors["phoneNumber"] && errors["phoneNumber"].message}
							</MessageStyles>
						) : null}

						<FormElementContainer>
							<FormElementLabel>Address Line 1</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"address"}
									defaultValue={data.general.address}
									control={control}
									as={<FormTextArea></FormTextArea>}
								/>
							</FormInputContainer>
						</FormElementContainer>

						<FormElementContainer>
							<FormElementLabel>State</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"state"}
									control={control}
									defaultValue={data.general.state}
									render={props => (
										<DropDown
											isFirst={true}
											variant='primary'
											items={australianStates}
											label='State'
											onChange={item => props.onChange(item.value)}
											initialSelectedItem={
												data
													? australianStates.filter(
															state => state.value === data.general.state
													  )[0]
													: ""
											}
											menuStyle={{position: "absolute", zIndex: 2}}
										/>
									)}
								/>
							</FormInputContainer>
						</FormElementContainer>

						<FormElementContainer>
							<FormElementLabel>City/suburb</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"city"}
									defaultValue={data.general.city}
									control={control}
									as={<FormTextInput id={"city"} type='text' placeholder='City' />}
								/>
							</FormInputContainer>
						</FormElementContainer>

						<FormElementContainer>
							<FormElementLabel>Postcode</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"postCode"}
									control={control}
									rules={{
										min: {
											value: 0,
											message: "Post Code can not be negative"
										}
									}}
									defaultValue={data.general.postCode}
									as={<FormTextInput id={"postCode"} type='number' placeholder='PostCode' />}
								/>
							</FormInputContainer>
						</FormElementContainer>
						{errors["postCode"] && errors["postCode"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["postCode"]}>
								{errors["postCode"] && errors["postCode"].message}
							</MessageStyles>
						) : null}

						<FormElementContainer>
							<FormElementLabel>Description</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"description"}
									defaultValue={data.general.description}
									control={control}
									rules={{
										maxLength: {
											value: 500,
											message: "Description can not exceed 500 characters"
										}
									}}
									as={<FormTextArea></FormTextArea>}
								/>
							</FormInputContainer>
						</FormElementContainer>
						{errors["description"] && errors["description"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["description"]}>
								{errors["description"] && errors["description"].message}
							</MessageStyles>
						) : null}
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer error={errors.salesManager?.type === "required"}>
							<FormElementLabel isRequired={true}>Sales Manager</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"salesManager"}
									control={control}
									defaultValue={data.general.salesManager}
									rules={{required: true}}
									render={props => (
										<DropDown
											isFirst={true}
											variant='primary'
											items={salesManager}
											label='Sales Manager'
											onChange={item => props.onChange(item.value)}
											initialSelectedItem={
												data
													? salesManager.filter(state =>
															state.value.includes(data.general.salesManager)
													  )[0]
													: data.general.salesManager
													? {
															label: data.general.salesManager,
															value: data.general.salesManager
													  }
													: ""
											}
											menuStyle={{position: "absolute", zIndex: 2}}
										/>
									)}
								/>
							</FormInputContainer>
						</FormElementContainer>

						{errors["salesManager"]?.message && (
							<MessageStyles htmlFor={1} focused={false} validation={errors["size"]}>
								{errors["salesManager"]?.message}
							</MessageStyles>
						)}

						<FormElementContainer error={errors.eapStatus?.type === "required"}>
							<FormElementLabel isRequired={true}>EAP Status</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"eapStatus"}
									control={control}
									defaultValue={data.general.eapStatus}
									rules={{required: true}}
									render={props => (
										<DropDown
											isFirst={true}
											variant='primary'
											items={[
												{
													label: "Uprise is the EAP",
													value: "uprise"
												},
												{
													label: "Uprise is Not the EAP",
													value: "not-uprise"
												}
											]}
											onChange={item => props.onChange(item.value)}
											label='EAP Status'
											initialSelectedItem={
												data
													? [
															{
																label: "Uprise is the EAP",
																value: "uprise"
															},
															{
																label: "Uprise is Not the EAP",
																value: "not-uprise"
															}
													  ].filter(state => state.value === data.general.eapStatus)[0]
													: ""
											}
											menuStyle={{position: "absolute", zIndex: 2}}
										/>
									)}
								/>
							</FormInputContainer>
						</FormElementContainer>

						{errors["eapStatus"]?.message && (
							<MessageStyles htmlFor={1} focused={false} validation={errors["size"]}>
								{errors["eapStatus"]?.message}
							</MessageStyles>
						)}

						{watchEAPStatus && watchEAPStatus === "ibh" ? (
							<Fragment>
								<FormElementContainer>
									<FormElementLabel>EAP name</FormElementLabel>
									<FormInputContainer>
										<Controller
											name={"eapname"}
											defaultValue={data.general?.eapname}
											control={control}
											rules={{required: "Please Select EAP Service"}}
											render={props => (
												<DropDown
													isFirst={true}
													items={eapdata}
													label='EAP Service'
													variant='primary'
													onChange={item => {
														_handleEAPNameChange(item)
														props.onChange(item.value)
													}}
													initialSelectedItem={
														eapdata.length > 0
															? eapdata.filter(
																	state => state.value === data.general.eapname
															  )[0]
															: {label: data.general.eapname, value: data.general.eapname}
													}
													menuStyle={{position: "absolute", zIndex: 2}}
												/>
											)}
										/>
									</FormInputContainer>
								</FormElementContainer>
								{errors["eapname"] && errors["eapname"].message ? (
									<MessageStyles htmlFor={1} focused={false} validation={errors["eapname"]}>
										{errors["eapname"] && errors["eapname"].message}
									</MessageStyles>
								) : null}

								{errors["eapname"]?.message && (
									<MessageStyles htmlFor={1} focused={false} validation={errors["size"]}>
										{errors["eapname"]?.message}
									</MessageStyles>
								)}

								<FormElementContainer>
									<FormElementLabel>EAP Phone</FormElementLabel>
									<FormInputContainer>
										<Controller
											name={"eapphone"}
											control={control}
											defaultValue={data.general.eapphone}
											as={
												<FormTextInput
													type='tel'
													id={"eapphone"}
													disabled={true}
													placeholder='EAP Phone Number'
												/>
											}
										/>
									</FormInputContainer>
								</FormElementContainer>

								<FormElementContainer>
									<FormElementLabel>EAP website</FormElementLabel>
									<FormInputContainer>
										<Controller
											name={"eapwebsite"}
											control={control}
											defaultValue={data.general.eapwebsite}
											as={
												<FormTextInput
													id={"eapwebsite"}
													type='text'
													placeholder='EAP website'
												/>
											}
										/>
									</FormInputContainer>
								</FormElementContainer>
							</Fragment>
						) : null}

						<FormElementContainer error={errors.launchDate?.type === "required"}>
							<FormElementLabel isRequired={true}>Launch date</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"launchDate"}
									control={control}
									rules={{required: true}}
									defaultValue={moment(data.general.launchDate)}
									render={props => {
										return (
											<Datepicker
												isFirst={true}
												date={props.value}
												onChange={e => props.onChange(e.target.value)}
											/>
										)
									}}
								/>
							</FormInputContainer>
						</FormElementContainer>

						{errors["launchDate"]?.message && (
							<MessageStyles htmlFor={1} focused={false} validation={errors["size"]}>
								{errors["launchDate"]?.message}
							</MessageStyles>
						)}

						<FormElementContainer error={errors.accountManager?.type === "required"}>
							<FormElementLabel isRequired={true}>Account Manager</FormElementLabel>

							<FormInputContainer>
								<Controller
									name={"accountManager"}
									control={control}
									defaultValue={data.general.accountManager}
									rules={{required: "Please Select an Account Manager", value: true}}
									render={props => (
										<DropDown
											isFirst={true}
											variant='primary'
											items={accountManager}
											label='Select Account Manager'
											onChange={item => props.onChange(item.value)}
											initialSelectedItem={
												data
													? accountManager.filter(state =>
															state.value.includes(data.general.accountManager)
													  )[0] || accountManager[5]
													: null
											}
											menuStyle={{position: "absolute", zIndex: 2}}
										/>
									)}
								/>
							</FormInputContainer>
						</FormElementContainer>

						{watchAccountManager === "Other" && (
							<>
								<FormElementContainer error={errors.other?.type === "required"}>
									<FormElementLabel isRequired={true}>Other</FormElementLabel>
									<FormInputContainer>
										<Controller
											name={"other"}
											data-testid='other'
											defaultValue={
												accountManager.some(
													state => state.value === data.general.accountManager
												)
													? ""
													: data.general.accountManager
											}
											rules={{
												required: {
													value: true,
													message: "Please enter a name"
												},
												maxLength: {
													value: 100,
													message: "Name can not exceed 100 characters"
												}
											}}
											control={control}
											as={<TextInputHorizontal id='other' type='text' placeholder='Other' />}
										/>
									</FormInputContainer>
								</FormElementContainer>
								{errors["other"] && errors["other"].message ? (
									<MessageStyles htmlFor={1} focused={false} validation={errors["other"]}>
										{errors["other"] && errors["other"].message}
									</MessageStyles>
								) : null}
							</>
						)}

						{errors["accountManager"]?.message && (
							<MessageStyles htmlFor={1} focused={false} validation={errors["size"]}>
								{errors["accountManager"]?.message}
							</MessageStyles>
						)}

						<FormElementContainer>
							<FormElementLabel>Notes</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"notes"}
									control={control}
									rules={{
										maxLength: {
											value: 500,
											message: "Notes can not exceed 500 characters"
										}
									}}
									defaultValue={data.general.notes}
									as={<FormTextArea></FormTextArea>}
								/>
							</FormInputContainer>
						</FormElementContainer>
						{errors["notes"]?.message && (
							<MessageStyles htmlFor={1} focused={false} validation={errors["size"]}>
								{errors["notes"]?.message}
							</MessageStyles>
						)}
					</Col>
				</Row>

				{/* ================= Contract Section - Start ======================= */}
				<Row>
					<Col className='col-12 col-lg-12 col-md-6 col-sm-12'>
						<H5>Contracts</H5>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer error={errors.contractStatus?.type === "required"}>
							<FormElementLabel isRequired={true}>Contract Status</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"contractStatus"}
									control={control}
									rules={{required: true}}
									defaultValue={data?.contract?.contractStatus}
									render={props => (
										<DropDown
											isFirst={true}
											variant='primary'
											items={[
												{
													label: "Current",
													value: "current"
												},
												{
													label: "Expired",
													value: "expired"
												}
											]}
											label='Contract Status'
											onChange={item => props.onChange(item.value)}
											initialSelectedItem={
												data
													? [
															{
																label: "Current",
																value: "current"
															},
															{
																label: "Expired",
																value: "expired"
															}
													  ].filter(
															state => state.value === data?.contract?.contractStatus
													  )?.[0]
													: ""
											}
											menuStyle={{position: "absolute", zIndex: 2}}
										/>
									)}
								/>
							</FormInputContainer>
						</FormElementContainer>
						<FormElementContainer error={errors.dateSigned?.type == "required"}>
							<FormElementLabel isRequired={true}>Date Signed</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"dateSigned"}
									control={control}
									rules={{required: true, validate: codeDateCheck}}
									defaultValue={moment(data?.contract?.dateSigned)}
									render={props => {
										return (
											<Datepicker
												isFirst={true}
												date={props.value}
												onChange={e => props.onChange(e.target.value)}
											/>
										)
									}}
								/>
							</FormInputContainer>
						</FormElementContainer>
						{errors["dateSigned"] && errors["dateSigned"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["dateSigned"]}>
								{errors["dateSigned"] && errors["dateSigned"].message}
							</MessageStyles>
						) : null}

						<FormElementContainer error={errors.expiryDate?.type === "required"}>
							<FormElementLabel isRequired={true}>Date of Expiry</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"expiryDate"}
									control={control}
									rules={{required: true, validate: codeDateCheckExpiry}}
									defaultValue={moment(data?.contract?.expiryDate)}
									render={props => {
										return (
											<Datepicker
												isFirst={true}
												date={props.value}
												onChange={e => props.onChange(e.target.value)}
											/>
										)
									}}
								/>
							</FormInputContainer>
						</FormElementContainer>
						{errors["expiryDate"] && errors["expiryDate"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["expiryDate"]}>
								{errors["expiryDate"] && errors["expiryDate"].message}
							</MessageStyles>
						) : null}
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel>Link to Pipedrive</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"pipedrive"}
									control={control}
									defaultValue={data?.contract?.pipedrive}
									as={<FormTextInput type='text' placeholder='Link to Pipedrive' />}
								/>
							</FormInputContainer>
						</FormElementContainer>

						<FormElementContainer>
							<FormElementLabel>Link to Gdrive</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"gdrive"}
									defaultValue={data?.contract?.gdrive}
									control={control}
									as={<FormTextInput type='text' placeholder='Link to Gdrive' />}
								/>
							</FormInputContainer>
						</FormElementContainer>
					</Col>
				</Row>

				{/* ========= Contract Section - END ======================== */}

				{/* ========= Contact Section - Start ================= */}

				{fields.map((el, index) => {
					return (
						<div key={index}>
							<H4>Contacts {index + 1}</H4>
							<Row>
								<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
									<FormElementContainer
										error={
											errors &&
											errors.contact &&
											errors.contact[index]?.firstName?.type === "required"
										}
									>
										<FormElementLabel isRequired={true}>First Name</FormElementLabel>
										<FormInputContainer>
											<Controller
												name={`contact[${index}].firstName`}
												rules={{
													required: {
														value: true,
														message: "FirstName is Required"
													},
													validate: validateName,
													pattern: {
														value: /^[a-zA-Z '.-]*$/g,
														message:
															"Only alphabets and the following characters are allowed: , ' -"
													}
												}}
												defaultValue={el.firstName}
												control={control}
												as={
													<FormTextInput
														type='text'
														placeholder='First Name'
														ref={register({required: true})}
													/>
												}
											/>
										</FormInputContainer>
									</FormElementContainer>

									{errors["contact"]?.[index]?.firstName?.message && (
										<MessageStyles
											htmlFor={`contact[${index}].firstName`}
											focused={false}
											validation={errors["contact"]?.[index]?.firstName}
										>
											{errors["contact"]?.[index]?.firstName?.message}
										</MessageStyles>
									)}

									<FormElementContainer
										error={errors?.contact?.[index]?.lastName?.type === "required"}
									>
										<FormElementLabel isRequired={true}>Last Name</FormElementLabel>
										<FormInputContainer>
											<Controller
												control={control}
												defaultValue={el.lastName}
												name={`contact[${index}].lastName`}
												rules={{
													required: {
														value: true,
														message: "LastName is Required"
													},
													validate: validateName,
													pattern: {
														value: /^[a-zA-Z '.-]*$/g,
														message:
															"Only alphabets and the following characters are allowed: , ' -"
													}
												}}
												as={<FormTextInput type='text' placeholder='Last Name' />}
											/>
										</FormInputContainer>
									</FormElementContainer>

									{errors["contact"]?.[index]?.lastName?.message && (
										<MessageStyles
											htmlFor={1}
											focused={false}
											validation={errors["contact"]?.[index]?.lastName}
										>
											{errors["contact"]?.[index]?.lastName?.message}
										</MessageStyles>
									)}

									<FormElementContainer>
										<FormElementLabel>Position title</FormElementLabel>
										<FormInputContainer>
											<Controller
												control={control}
												defaultValue={el.positionTitle}
												name={`contact[${index}].positionTitle`}
												as={<FormTextInput type='text' placeholder='Position title' />}
											/>
										</FormInputContainer>
									</FormElementContainer>

									{errors["contact"]?.[index]?.positionTitle?.message && (
										<MessageStyles
											htmlFor={1}
											focused={false}
											validation={errors["contact"]?.[index]?.positionTitle}
										>
											{errors["contact"]?.[index]?.positionTitle?.message}
										</MessageStyles>
									)}

									<FormElementContainer
										error={errors?.contact?.[index]?.phoneNumber?.type === "required"}
									>
										<FormElementLabel isRequired={true}>Phone Number</FormElementLabel>
										<FormInputContainer>
											<Controller
												control={control}
												defaultValue={el.phoneNumber}
												name={`contact[${index}].phoneNumber`}
												rules={{
													required: {
														value: true,
														message: "Contact phone number is Required"
													},
													pattern: {
														value: /^\+?[\d ]+$/g,
														message: "Phone number should be valid"
													},
													maxLength: {
														value: 15,
														message: "Phone Number can be a maximum of 15 characters"
													}
												}}
												as={<FormTextInput type='tel' placeholder='Phone Number' />}
											/>
										</FormInputContainer>
									</FormElementContainer>

									{errors["contact"]?.[index]?.phoneNumber?.message && (
										<MessageStyles
											htmlFor={1}
											focused={false}
											validation={errors?.contact?.[index]?.phoneNumber}
										>
											{errors["contact"]?.[index]?.phoneNumber?.message}
										</MessageStyles>
									)}
								</Col>
								<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
									<FormElementContainer
										error={errors?.contact?.[index]?.emailAddress?.type === "required"}
									>
										<FormElementLabel isRequired={true}>Email address</FormElementLabel>
										<FormInputContainer>
											<Controller
												control={control}
												defaultValue={el.emailAddress}
												name={`contact[${index}].emailAddress`}
												rules={{required: true}}
												as={<FormTextInput type='email' placeholder='Email address' />}
											/>
										</FormInputContainer>
									</FormElementContainer>

									{errors["contact"]?.[index]?.emailAddress?.message && (
										<MessageStyles
											htmlFor={1}
											focused={false}
											validation={errors["contact"]?.[index]?.emailAddress}
										>
											{errors["contact"]?.[index]?.emailAddress?.message}
										</MessageStyles>
									)}

									<FormElementContainer
										error={errors?.contact?.[index]?.contactFunction?.type === "required"}
									>
										<FormElementLabel isRequired={true}>Contact function</FormElementLabel>
										<FormInputContainer>
											<Controller
												defaultValue={el.contactFunction}
												name={`contact[${index}].contactFunction`}
												rules={{required: true}}
												render={props => (
													<DropDown
														isFirst={true}
														variant='primary'
														items={[
															{
																label: "Main POC",
																value: "poc"
															},
															{
																label: "Budget Alert",
																value: "budget_alert"
															},
															{
																label: "TCA",
																value: "TCA"
															},
															{
																label: "MHFA(Mental Health First Aiders)",
																value: "MHFA"
															}
														]}
														label='Contact Function'
														initialSelectedItem={
															data
																? [
																		{
																			label: "Main POC",
																			value: "poc"
																		},
																		{
																			label: "Budget Alert",
																			value: "budget_alert"
																		},
																		{
																			label: "TCA",
																			value: "TCA"
																		},
																		{
																			label: "MHFA(Mental Health First Aiders)",
																			value: "MHFA"
																		}
																  ].filter(
																		state => state.value === el.contactFunction
																  )[0]
																: ""
														}
														onChange={item => props.onChange(item.value)}
														menuStyle={{position: "absolute", zIndex: 2}}
													/>
												)}
												control={control}
											/>
										</FormInputContainer>
									</FormElementContainer>

									{errors["contact"]?.[index]?.contactFunction?.message && (
										<MessageStyles
											htmlFor={1}
											focused={false}
											validation={errors["contact"]?.[index]?.contactFunction}
										>
											{errors["contact"]?.[index]?.contactFunction?.message}
										</MessageStyles>
									)}

									<FormElementContainer
										error={errors?.contact?.[index]?.contactTier?.type === "required"}
									>
										<FormElementLabel isRequired={true}>Contact tier</FormElementLabel>
										<FormInputContainer>
											<Controller
												defaultValue={el.contactTier ? el.contactTier : 1}
												name={`contact[${index}].contactTier`}
												rules={{required: true}}
												render={props => (
													<DropDown
														isFirst={true}
														variant='primary'
														items={[
															{
																label: "1st Tier",
																value: 1
															},
															{
																label: "2nd Tier",
																value: 2
															},
															{
																label: "3rd Tier",
																value: 3
															}
														]}
														label='Tier'
														onChange={item => props.onChange(item.value)}
														initialSelectedItem={
															data && el.contactTier
																? [
																		{
																			label: "1st Tier",
																			value: 1
																		},
																		{
																			label: "2nd Tier",
																			value: 2
																		},
																		{
																			label: "3rd Tier",
																			value: 3
																		}
																  ].filter(state => state.value === el.contactTier)[0]
																: {
																		label: "1st Tier",
																		value: 1
																  }
														}
														menuStyle={{position: "absolute", zIndex: 2}}
													/>
												)}
												control={control}
											/>
										</FormInputContainer>
									</FormElementContainer>
									<Col className='col-12 col-lg-12 col-md-12 col-sm-12 justify-content-end d-flex'>
										{fields.length > 1 && (
											<>
												{confirmDeleteIdx === index ? (
													<>
														<Button
															className={"m-t-2"}
															style={{marginLeft: "1rem"}}
															onClick={() => setConfirmDeleteIdx(-1)}
															size={"medium"}
															variant={"secondary"}
															title={"Cancel"}
														/>
														<ButtonWarning
															className={"m-t-2"}
															style={{marginLeft: "1rem"}}
															onClick={e => handleDeleteContact(e, index)}
															size={"medium"}
															variant={"tertiary"}
															title={"Delete"}
														/>
													</>
												) : (
													<ButtonWarning
														className={"m-t-2"}
														style={{marginLeft: "1rem"}}
														onClick={() => setConfirmDeleteIdx(index)}
														size={"medium"}
														variant={"tertiary"}
														title={"Delete"}
													/>
												)}
											</>
										)}
									</Col>
								</Col>
							</Row>
						</div>
					)
				})}
				<Row>
					<ButtonContainer>
						<NavigationButton onClick={handleAddContact} variant={"primary"} title={"Add Contact"} />
					</ButtonContainer>
				</Row>

				{/* ========= Contact Section - End =============== */}

				{/* ============================= Code & Caps Section ===================================== */}
				<Row>
					<Col className='col-12 col-lg-12 col-md-6 col-sm-12'>
						<H5>Code</H5>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer error={errors?.employerCode?.type === "required"}>
							<FormElementLabel isRequired={true}>Employer Code</FormElementLabel>

							<FormInputContainer>
								<Controller
									name={"employerCode"}
									control={control}
									rules={{required: true}}
									defaultValue={employerCode}
									as={
										<FormTextInput
											type='text'
											disabled='true'
											placeholder='Employer Code'
											// name='employerCode'
											// value={state.employerCode}
											// onChange={onChange}
										/>
									}
								/>
							</FormInputContainer>
						</FormElementContainer>

						<FormElementContainer error={errors.codeType?.type === "required"}>
							<FormElementLabel isRequired={true}>Code Type</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"codeType"}
									control={control}
									rules={{required: true}}
									defaultValue={data.code.codeType}
									render={props => (
										<DropDown
											isFirst={true}
											variant='primary'
											items={[
												{
													label: "Regular",
													value: "regular"
												},
												//{
												//	label: "Master",
												//	value: "master"
												//},
												{
													label: "Family",
													value: "family"
												}
											]}
											label='Code Type'
											onChange={item => props.onChange(item.value)}
											initialSelectedItem={
												data
													? [
															{
																label: "Regular",
																value: "regular"
															},
															//{
															//	label: "Master",
															//	value: "master"
															//},
															{
																label: "Family",
																value: "family"
															}
													  ].filter(state => state.value === data.code.codeType)[0]
													: ""
											}
											menuStyle={{position: "absolute", zIndex: 2}}
										/>
									)}
								/>
							</FormInputContainer>
						</FormElementContainer>

						{/*watchCodeType && watchCodeType !== "master" ? (
							<FormElementContainer>
								<FormElementLabel>Link to master code</FormElementLabel>
								<FormInputContainer>
									<Controller
										name={"masterCode"}
										control={control}
										defaultValue={data.code.masterCode}
										// as={<FormTextInput type='text' placeholder='' />}
										render={props => (
											<DropDown
												isFirst={true}
												variant='primary'
												items={masterCodes.map(code => ({
													label: code,
													value: code
												}))}
												label={data.code.masterCode}
												onChange={item => props.onChange(item.value)}
												initialSelectedItem={
													data
														? masterCodes &&
														  masterCodes
																.filter(state => state === data.code.masterCode)
																.map(code => ({
																	label: code,
																	value: code
																}))[0]
														: ""
												}
												menuStyle={{position: "absolute", zIndex: 2}}
											/>
										)}
									/>
								</FormInputContainer>
							</FormElementContainer>
						) : null */}

						<FormElementContainer>
							<FormElementLabel>Code Label</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"codeLabel"}
									control={control}
									defaultValue={data.code.codeLabel}
									as={
										<FormTextInput
											type='text'
											placeholder='Label'
											// name='codeLabel'
											// value={state.codeLabel}
											// onChange={onChange}
										/>
									}
								/>
							</FormInputContainer>
						</FormElementContainer>

						<FormElementContainer error={errors.codeStartDate?.type === "required"}>
							<FormElementLabel isRequired={true}>Code start date</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"codeStartDate"}
									control={control}
									rules={{required: true, validate: planDateCheck}}
									defaultValue={moment(data.code.codeStartDate)}
									// valueName='selected' // DateSelect value's name is selected
									render={props => {
										return (
											<Datepicker
												isFirst={true}
												date={props.value}
												onChange={e => {
													setLatestCapRecords({
														...latestCapRecords,
														startingDate: e.target.value
													})
													props.onChange(e.target.value)
												}}
											/>
										)
									}}
								/>
							</FormInputContainer>
						</FormElementContainer>

						{errors["codeStartDate"]?.message && (
							<MessageStyles htmlFor={1} focused={false} validation={errors["codeStartDate"]}>
								{errors["codeStartDate"]?.message}
							</MessageStyles>
						)}

						<FormElementContainer error={errors.codeExpiryDate?.type === "required"}>
							<FormElementLabel isRequired={true}>Code expiry date</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"codeExpiryDate"}
									control={control}
									rules={{required: true, validate: planDateCheckExpiry}}
									defaultValue={moment(expiryDate)}
									render={props => {
										return (
											<Datepicker
												isFirst={true}
												date={props.value}
												onChange={e => props.onChange(e.target.value)}
											/>
										)
									}}
								/>
							</FormInputContainer>
						</FormElementContainer>

						{errors["codeExpiryDate"]?.message && (
							<MessageStyles htmlFor={1} focused={false} validation={errors["codeExpiryDate"]}>
								{errors["codeExpiryDate"]?.message}
							</MessageStyles>
						)}

						<FormElementContainer error={errors.course?.type === "required"}>
							<FormElementLabel isRequired={true}>Course</FormElementLabel>
							<FormInputContainer>
								<Controller
									control={control}
									name={"course"}
									rules={{required: true}}
									defaultValue={data.code.course}
									render={props => (
										<DropDown
											isFirst={true}
											variant='primary'
											items={courses.map(course => {
												return {
													label: course,
													value: course
												}
											})}
											label='Select Course'
											onChange={item => {
												props.onChange(item.value)
											}}
											initialSelectedItem={
												data
													? courses
															.map(course => {
																return {
																	label: course,
																	value: course
																}
															})
															.filter(state =>
																state.value.includes(
																	data.code.course ? data.code.course : data.course
																)
															)[0]
													: ""
											}
											menuStyle={{position: "absolute", zIndex: 2}}
										/>
									)}
								/>
							</FormInputContainer>
						</FormElementContainer>
					</Col>

					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer error={errors.maxUser?.type === "required"}>
							<FormElementLabel isRequired={true}>Max users</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"maxUser"}
									control={control}
									rules={{
										required: {
											value: true,
											message: "Max Users is Required"
										},
										min: {
											value: 1,
											message: "Max Users should be at least 1"
										}
									}}
									defaultValue={data.code.maxUser}
									as={
										<FormTextInput
											type='number'
											placeholder='Max users'
											// name='maxUser'
											// value={state.maxUser}
											// onChange={onChange}
										/>
									}
								/>
							</FormInputContainer>
						</FormElementContainer>

						{errors["maxUser"]?.message && (
							<MessageStyles htmlFor={1} focused={false} validation={errors["maxUser"]}>
								{errors["maxUser"]?.message}
							</MessageStyles>
						)}

						<FormElementContainer>
							<FormElementLabel>Cap calls</FormElementLabel>
							<ToggleContainer>
								<Controller
									name={"capCalls"}
									control={control}
									defaultValue={capDetails?.capCalls}
									render={({onChange, ref, value}) => (
										<Toggle
											checked={value}
											onChange={() => {
												onChange(!value)
											}}
											backgroundColorChecked={"#7d60ff"}
											width='42'
											height='24'
											InputRef={ref}
											sliderHeight='20.8'
											sliderWidth='20.8'
											translate='18'
										/>
									)}
								/>
							</ToggleContainer>
						</FormElementContainer>

						<FormElementContainer error={errors.coachingType?.type === "required"}>
							<FormElementLabel isRequired={true}>Session Cap</FormElementLabel>
							<FormInputInlineContainer>
								<Controller
									name={"coachingType"}
									control={control}
									rules={{required: "Session Cap is required"}}
									defaultValue={"combined"}
									render={props => (
										<>
											<Radio
												checked={watchSessionCap.coachingType === "combined"}
												label={"Total Calls"}
												value={"combined"}
												disabled={isError}
												onChange={e =>
													setValue("coachingType", "combined", {shouldValidate: true})
												}
												name={"coachingType"}
												width={"120px"}
											/>
											<Radio
												checked={watchSessionCap.coachingType === "individual"}
												label={"Coaching and Therapy"}
												disabled={isError}
												value={"individual"}
												onChange={e =>
													setValue("coachingType", "individual", {shouldValidate: true})
												}
												name={"coachingType"}
												width={"180px"}
											/>
										</>
									)}
								/>
							</FormInputInlineContainer>
						</FormElementContainer>

						{errors["coachingType"]?.message && (
							<MessageStyles htmlFor={1} focused={false} validation={errors["coachingType"]}>
								{errors["coachingType"].message}
							</MessageStyles>
						)}

						{watchSessionCap.coachingType === "combined" ? (
							<>
								<TextInputWrap>
									<NumberInputContainer
										onChange={e => {
											setLatestCapRecords({
												...latestCapRecords,
												combinedBookingCaps: e.target.value
											})
										}}
										error={errors.combinedBookingCaps?.type === "required"}
									>
										<FormSpanLabel>
											<Controller
												name={"combinedBookingCaps"}
												control={control}
												rules={{
													required: {
														value: true,
														message: "Sessions is required"
													},
													min: {
														value: 0,
														message: "Sessions should be at least 0"
													},
													maxLength: {
														value: 5,
														message: "Sessions cant exceed 99,999"
													}
												}}
												defaultValue={() => {
													return calcFinalCaps(callCapPeriod)?.combinedBookingCaps
												}}
												as={
													<FormNumberInputFixedWidth
														type='number'
														disabled={!watchCapCalls.capCalls || isError}
													/>
												}
											/>
											<FormSpan>
												<FormSpanText fontSize='15px' weight={"bold"}>
													sessions
												</FormSpanText>
											</FormSpan>
										</FormSpanLabel>
									</NumberInputContainer>
								</TextInputWrap>

								{errors?.["combinedBookingCaps"]?.message && (
									<MessageStylesCaps
										htmlFor={1}
										focused={false}
										validation={errors["combinedBookingCaps"]}
									>
										{errors["combinedBookingCaps"]?.message}
									</MessageStylesCaps>
								)}
							</>
						) : (
							<>
								<TextInputWrap>
									<NumberInputContainer
										onChange={e => {
											setLatestCapRecords({
												...latestCapRecords,
												coachingBookingCaps: e.target.value
											})
										}}
										error={errors["coachingBookingCaps"]?.type === "required"}
									>
										<FormSpanLabel>
											<Controller
												name={"coachingBookingCaps"}
												control={control}
												rules={{
													required: {
														value: true,
														message: "Coaching is required"
													},
													min: {
														value: 0,
														message: "Coaching sessions should be at least 0"
													},
													maxLength: {
														value: 5,
														message: "Coaching Sessions cant exceed 99,999"
													}
												}}
												defaultValue={calcFinalCaps(callCapPeriod)?.coachingBookingCaps}
												as={
													<FormNumberInputFixedWidth
														type='number'
														disabled={!watchCapCalls.capCalls || isError}
													/>
												}
											/>
											<FormSpan>
												<FormSpanText fontSize='15px' weight={"bold"}>
													coaching
												</FormSpanText>
											</FormSpan>
										</FormSpanLabel>
									</NumberInputContainer>

									<NumberInputContainer
										onChange={e => {
											setLatestCapRecords({
												...latestCapRecords,
												therapyBookingCaps: e.target.value
											})
										}}
										error={errors.therapyBookingCaps?.type === "required"}
									>
										<FormSpanLabel>
											<Controller
												name={"therapyBookingCaps"}
												control={control}
												rules={{
													required: {
														value: true,
														message: "Therapy is required"
													},
													min: {
														value: 0,
														message: "Therapy sessions should be at least 0"
													},
													maxLength: {
														value: 6,
														message: "Therapy Sessions cant exceed 99,999"
													}
												}}
												defaultValue={calcFinalCaps(callCapPeriod)?.therapyBookingCaps}
												as={
													<FormNumberInputFixedWidth
														type='number'
														disabled={!watchCapCalls.capCalls || isError}
													/>
												}
											/>
											<FormSpan>
												<FormSpanText fontSize='15px' weight={"bold"}>
													therapy
												</FormSpanText>
											</FormSpan>
										</FormSpanLabel>
									</NumberInputContainer>
								</TextInputWrap>

								{errors["coachingBookingCaps"]?.message && (
									<MessageStylesCaps
										htmlFor={1}
										focused={false}
										validation={errors["coachingBookingCaps"]}
									>
										{errors["coachingBookingCaps"]?.message}
									</MessageStylesCaps>
								)}
							</>
						)}

						<FormElementContainer>
							<FormElementLabel>Budget Alert</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"budgetAlert"}
									control={control}
									render={props => (
										<DropDown
											isFirst={true}
											variant='primary'
											items={[
												{
													label: "Dollar Amount",
													value: "dollar"
												},
												{
													label: "Hours Remaining",
													value: "hours"
												}
											]}
											label='Select Budget Alert'
											onChange={item => {
												return props.onChange(item.value)
											}}
											initialSelectedItem={
												data
													? [
															{
																label: "Dollar Amount",
																value: "dollar"
															},
															{
																label: "Hours Remaining",
																value: "hours"
															}
													  ].filter(state => state.value === data.code.budgetAlert)[0]
													: ""
											}
											menuStyle={{position: "absolute", zIndex: 2}}
										/>
									)}
								/>
							</FormInputContainer>
						</FormElementContainer>
						<Fragment>
							{watchBudgetAlert && watchBudgetAlert === "dollar" ? (
								<FormElementContainer>
									<FormElementLabel>Enter Amount(AUD)</FormElementLabel>
									<FormInputContainer>
										<Controller
											name={"budgetAmount"}
											control={control}
											rules={{
												min: {
													value: 0,
													message: "Amount can not be negative"
												}
											}}
											defaultValue={data.code.budgetAmount}
											as={<FormTextInput type='number' placeholder='Budget Amount' />}
										/>
									</FormInputContainer>
								</FormElementContainer>
							) : watchBudgetAlert && watchBudgetAlert === "hours" ? (
								<FormElementContainer>
									<FormElementLabel>Enter No of Hours</FormElementLabel>
									<FormInputContainer>
										<Controller
											name={"budgetHours"}
											control={control}
											rules={{
												min: {
													value: 0,
													message: "Hours can not be negative"
												}
											}}
											defaultValue={data.code.budgetHours}
											as={<FormTextInput type='number' placeholder='Budget Hours' />}
										/>
									</FormInputContainer>
								</FormElementContainer>
							) : null}
							{errors["budgetAmount"] && errors["budgetAmount"].message ? (
								<MessageStyles htmlFor={1} focused={false} validation={errors["budgetAmount"]}>
									{errors["budgetAmount"] && errors["budgetAmount"].message}
								</MessageStyles>
							) : null}

							{errors["budgetHours"] && errors["budgetHours"].message ? (
								<MessageStyles htmlFor={1} focused={false} validation={errors["budgetHours"]}>
									{errors["budgetHours"] && errors["budgetHours"].message}
								</MessageStyles>
							) : null}
						</Fragment>
						<FormElementContainer>
							<FormElementLabel>If budget/hours exhausted</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"budgetexceedAction"}
									control={control}
									render={props => (
										<DropDown
											isFirst={true}
											variant='primary'
											items={[
												{
													label: "Disable booking",
													value: "disable-booking"
												},
												{
													label: "Bill in arrears",
													value: "bill-in-arrears"
												}
											]}
											label='Select'
											onChange={item => props.onChange(item.value)}
											initialSelectedItem={
												data
													? [
															{
																label: "Disable booking",
																value: "disable-booking"
															},
															{
																label: "Bill in arrears",
																value: "bill-in-arrears"
															}
													  ].filter(state => state.value === data.code.budgetexceedAction)[0]
													: ""
											}
											menuStyle={{position: "absolute", zIndex: 2}}
										/>
									)}
								/>
							</FormInputContainer>
						</FormElementContainer>

						<FormElementContainer>
							<FormElementLabel>Email domains</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"domains"}
									data-testid='domains'
									control={control}
									rules={{
										pattern: {
											value: /^\s*(?:(?:^@\w+(?:-+\w+)*\.)+[a-z]+)\s*(?:,\s*(?:(?:@\w+(?:-+\w+)*\.)+[a-z]+)\s*)*/g,
											message: "Domains should be seperated by commas: @domain.com, @domain2.com"
										}
									}}
									defaultValue={domains?.length ? domains.join(", ") : ""}
									as={
										<TextInputHorizontal
											id='domains'
											type='text'
											placeholder='@domain.com, @domain2.com'
										/>
									}
								/>
							</FormInputContainer>
						</FormElementContainer>
						{errors["domains"] && errors["domains"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["domains"]}>
								{errors["domains"] && errors["domains"].message}
							</MessageStyles>
						) : null}

						<FormElementContainer>
							<FormElementLabel>Email Exercising Results</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"emailResults"}
									control={control}
									defaultValue={data.code.emailResults}
									render={({onChange, ref, value}) => (
										<Toggle
											checked={value}
											// onChange={() => SetState({...state, emailResults: !state.emailResults})}
											backgroundColorChecked={"#7d60ff"}
											width='42'
											height='24'
											InputRef={ref}
											sliderHeight='20.8'
											sliderWidth='20.8'
											translate='18'
										/>
									)}
								/>
							</FormInputContainer>
						</FormElementContainer>
					</Col>
				</Row>
				{/* ============================= Code & Caps Section - END ===================================== */}

				{/* ============================= Plan Section - Start  =====================*/}
				<Row>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<H5>Plan</H5>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<H5>Add-on</H5>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel isRequired={true}>Pricing Type</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"pricingType"}
									control={control}
									defaultValue={data.plan.pricingType}
									rules={{required: true}}
									render={props => (
										<DropDown
											isLast={true}
											variant='primary'
											items={[
												{
													label: "Variable Fee",
													value: "variable"
												},
												{
													label: "Fixed Annual Fee",
													value: "fixed"
												}
											]}
											label='Pricing Type'
											onChange={item => props.onChange(item.value)}
											initialSelectedItem={
												data
													? [
															{
																label: "Variable Fee",
																value: "variable"
															},
															{
																label: "Fixed Annual Fee",
																value: "fixed"
															}
													  ].filter(state => state.value === data.plan.pricingType)[0]
													: ""
											}
											menuStyle={{position: "absolute", zIndex: 2}}
										/>
									)}
								/>
							</FormInputContainer>
						</FormElementContainer>

						<Fragment>
							<FormRightItemContainer>
								<FormElementLabel>Pricing</FormElementLabel>
								<FormInputContainer style={{maxWidth: "80px", margin: "16px 10px 8px"}}>
									<Controller
										control={control}
										name={"price"}
										rules={{
											min: {
												value: 0,
												message: "Cost cannot be negative"
											}
										}}
										defaultValue={data.plan.price}
										as={<FormTextInput type='number' placeholder='' />}
									/>
								</FormInputContainer>

								<FormElementLabel>Per</FormElementLabel>
								<FormInputContainer style={{maxWidth: "80px", margin: "16px 10px 8px"}}>
									<Controller
										control={control}
										name={"licenses"}
										defaultValue={data.plan.licenses}
										rules={{
											min: {
												value: 0,
												message: "Amount cannot be negative"
											}
										}}
										as={
											<FormTextInput
												type='number'
												placeholder=''
												// value={prepursachedHours}
												// onChange={e => SetPrePurchasedHours(e.target.value)}
											/>
										}
									/>
								</FormInputContainer>
								<FormElementLabel>Licenses</FormElementLabel>
							</FormRightItemContainer>
							{errors["price"] && errors["price"].message ? (
								<MessageStyles htmlFor={1} focused={false} validation={errors["price"]}>
									{errors["price"] && errors["price"].message}
								</MessageStyles>
							) : null}
							{errors["licenses"] && errors["licenses"].message ? (
								<MessageStyles htmlFor={1} focused={false} validation={errors["licenses"]}>
									{errors["licenses"] && errors["licenses"].message}
								</MessageStyles>
							) : null}{" "}
							{featurePricingFields.map((el, index) => {
								return (
									<Fragment key={index}>
										<PlanContainer>
											<Fragment>
												<div style={{maxWidth: "170px", minWidth: "170px"}}>
													<FormElementPlanLabel>
														<FormInputContainer style={{flexBasis: "20%"}}>
															<Controller
																name={`featurePricing.${index}.label`}
																control={control}
																defaultValue={el.label}
																render={props => <Fragment>{el.label}</Fragment>}
															/>
														</FormInputContainer>
													</FormElementPlanLabel>
												</div>
												<FormElementLabel style={{display: "hidden"}}>
													<Controller
														name={`featurePricing.${index}.id`}
														control={control}
														defaultValue={el.id}
														render={props => null}
													/>
												</FormElementLabel>
												<FeaturePricingInputContainer style={{margin: "0 12px 0 6px"}}>
													<Controller
														name={`featurePricing.${index}.enabled`}
														control={control}
														defaultValue={el.enabled}
														render={props => (
															<Toggle
																checked={props.value}
																onChange={() => {
																	props.onChange(!props.value)
																}}
																backgroundColorChecked={"#7d60ff"}
																width='42'
																height='24'
																sliderHeight='20.8'
																sliderWidth='20.8'
																translate='18'
															/>
														)}
													/>
												</FeaturePricingInputContainer>

												{(el.enabled || watchFeaturePricing[index].enabled) &&
												!["care-navigator", "additional-sessions"].includes(el.id) ? (
													<Fragment>
														<FormElementLabel style={{marginLeft: "10px"}}>
															Cost
														</FormElementLabel>
														<FormInputContainer
															style={{
																flexBasis: "10%",
																maxWidth: "80px",
																margin: "16px 10px 8px"
															}}
														>
															<Controller
																control={control}
																name={`featurePricing.${index}.fee`}
																rules={{
																	min: {
																		value: 0,
																		message: "Cost cannot be negative"
																	}
																}}
																defaultValue={el.fee}
																as={<FormTextInput type='number' placeholder='' />}
															/>
														</FormInputContainer>
														<FormElementLabel>per</FormElementLabel>
														<FormInputContainer style={{flexBasis: "20%"}}>
															<Controller
																name={`featurePricing.${index}.per`}
																control={control}
																defaultValue={
																	[
																		{
																			label: "Hour",
																			value: "hour"
																		},
																		{
																			label: "Per Fleet",
																			value: "fleet"
																		}
																	].filter(item => item.value === el.per)[0]
																		? [
																				{
																					label: "Hour",
																					value: "hour"
																				},
																				{
																					label: "Per Fleet",
																					value: "fleet"
																				}
																		  ].filter(item => item.value === el.per)[0]
																				.value
																		: ""
																}
																render={props => (
																	<DropDown
																		isFirst={true}
																		variant='primary'
																		items={[
																			{
																				label: "Hour",
																				value: "hour"
																			},
																			{
																				label: "Per Fleet",
																				value: "fleet"
																			}
																		]}
																		label='Select type'
																		onChange={item => {
																			props.onChange(item.value)
																		}}
																		initialSelectedItem={
																			data
																				? [
																						{
																							label: "Hour",
																							value: "hour"
																						},
																						{
																							label: "Per Fleet",
																							value: "fleet"
																						}
																				  ].filter(
																						state => state.value == el.per
																				  )[0]
																					? [
																							{
																								label: "Hour",
																								value: "hour"
																							},
																							{
																								label: "Per Fleet",
																								value: "fleet"
																							}
																					  ].filter(
																							state =>
																								state.value == el.per
																					  )[0]
																					: ""
																				: null
																		}
																		menuStyle={{position: "absolute", zIndex: 2}}
																	/>
																)}
															/>
														</FormInputContainer>
													</Fragment>
												) : null}
											</Fragment>
										</PlanContainer>
										{errors[`featurePricing`]?.[index]?.fee &&
										errors[`featurePricing`]?.[index]?.fee?.message ? (
											<MessageStyles
												htmlFor={1}
												focused={false}
												validation={errors[`featurePricing`]?.[index]?.fee}
											>
												{errors[`featurePricing`]?.[index]?.fee &&
													errors[`featurePricing`]?.[index]?.fee?.message}
											</MessageStyles>
										) : null}
									</Fragment>
								)
							})}
							{isError && (
								<ErrorWrap>
									<WarnWrap alt='' src={WarnIcon} />
									If Counselling and Therapy sessions are turned off, users won’t see any Booking
									options
								</ErrorWrap>
							)}
						</Fragment>
					</Col>
					<Col className='col-6 col-lg-6 col-md-6 col-sm-12'>
						<FormElementContainer>
							<FormElementLabel isRequired={true}>Add-on type</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"addOnType"}
									defaultValue={data.plan.addOnType}
									control={control}
									render={props => (
										<DropDown
											isLast={true}
											variant='primary'
											items={[
												{
													label: "Pre-Purchased Hours",
													value: "pre-purchased"
												}
											]}
											label='Select Add-on type'
											onChange={item => props.onChange(item.value)}
											initialSelectedItem={
												data
													? [
															{
																label: "Pre-Purchased Hours",
																value: "pre-purchased"
															}
													  ].filter(state => state.value == data.plan.addOnType)[0]
													: ""
											}
											menuStyle={{position: "absolute", zIndex: 2}}
										/>
									)}
								/>
							</FormInputContainer>
						</FormElementContainer>

						<FormElementContainer>
							<FormElementLabel isRequired={false}>Pre-purchased</FormElementLabel>
							<FormRightContainer>
								<FormInputContainer>
									<Controller
										control={control}
										rules={{
											min: {
												value: 0,
												message: "Hours cannot be negative"
											}
										}}
										defaultValue={data.plan.prePurchased}
										name={"prePurchased"}
										as={<FormTextInput type='number' placeholder='' />}
									/>
								</FormInputContainer>
								<FormSpanText color={"#6d6c79"}>hours</FormSpanText>
							</FormRightContainer>
						</FormElementContainer>
						{errors["prePurchased"] && errors["prePurchased"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["prePurchased"]}>
								{errors["prePurchased"] && errors["prePurchased"].message}
							</MessageStyles>
						) : null}

						<FormElementContainer>
							<FormElementLabel isRequired={false}>Price</FormElementLabel>
							<FormRightContainer>
								<FormInputContainer>
									<Controller
										control={control}
										name={"prePurchasedPrice"}
										rules={{
											min: {
												value: 0,
												message: "Price cannot be negative"
											}
										}}
										defaultValue={data.plan.prePurchasedPrice}
										as={<FormTextInput type='number' placeholder='' />}
									/>
								</FormInputContainer>
								<FormSpanText color={"#6d6c79"}>Per hour</FormSpanText>
							</FormRightContainer>
						</FormElementContainer>
						{errors["prePurchasedPrice"] && errors["prePurchasedPrice"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["prePurchasedPrice"]}>
								{errors["prePurchasedPrice"] && errors["prePurchasedPrice"].message}
							</MessageStyles>
						) : null}
					</Col>
				</Row>
				{/* =================== Plan Section - End ====================== */}
				<Row className='flex-row-reverse'>
					<ButtonContainer>
						<PrimaryButton type='submit' variant={"primary"} title={"Update"} />
					</ButtonContainer>
					<ButtonContainer>
						<SecondaryButton
							onClick={e => {
								e.preventDefault()
								onCancel()
							}}
							variant={"secondary"}
							title={"Cancel"}
						/>
					</ButtonContainer>
				</Row>
			</form>
		</Container>
	)
}

export default EditEmployerDetails
